import {type LocationDescriptorObject} from 'history';
import {useEffect} from 'react';
import {useHistory} from 'react-router-dom';

import {removeHashFromURL} from './urls';
import {isOnReportView} from './urls/reportPaths';

// Keep <link rel="canonical"> in sync with the current URL for third-party
// tools like Segment (wandb/core#10732). The initial write is skipped on
// report routes so we don't clobber the SSR-set canonical built from the
// live view.displayName (see host/src/meta.ts, SEO-119); later navigations
// still update the tag so SPA route changes stay tracked.
export function useUpdateCanonicalURL() {
  const history = useHistory();
  useEffect(() => {
    let unregister: () => void;

    function updateCanonicalURL(): void {
      const currentURL = removeHashFromURL(window.location.href);

      const canonicalTag = document.querySelector('link[rel="canonical"]');
      canonicalTag?.setAttribute(`href`, currentURL);
    }

    if (!(window as any).__WB_BOT_MODE) {
      if (!isOnReportView()) {
        updateCanonicalURL();
      }
      unregister = history.listen(updateCanonicalURL);
    }

    return () => {
      unregister?.();
    };
  }, [history]);
}

interface BaseNavigateOpts {
  replace?: boolean;
}

// Use this to navigate to a new path while optionally preserving search/hash
interface PathnameNavigateOpts extends BaseNavigateOpts {
  pathname: string;
  preserveSearch?: boolean;
  preserveHash?: boolean;
}

// Use this to navigate to a new location, including explicit new search/hash values
interface LocationNavigateOpts extends BaseNavigateOpts {
  location: LocationDescriptorObject;
}

type NavigateOpts = PathnameNavigateOpts | LocationNavigateOpts;

export function useNavigateTo() {
  const history = useHistory();
  return ({replace = false, ...opts}: NavigateOpts) => {
    if ('location' in opts) {
      if (replace) {
        history.replace(opts.location);
      } else {
        history.push(opts.location);
      }
      return;
    }

    if ('pathname' in opts) {
      const {pathname, preserveSearch = false, preserveHash = false} = opts;
      const {location} = history;

      const to: LocationDescriptorObject = {pathname};
      if (preserveSearch) {
        to.search = location.search;
      }
      if (preserveHash) {
        to.hash = location.hash;
      }

      if (replace) {
        history.replace(to);
      } else {
        history.push(to);
      }
      return;
    }
  };
}
